import React from 'react';
import { FormControl, FormLabel, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { Rectangle2D } from '@paragon/contract-library';
import { AngleField, DistanceField } from '@paragon/material';
import { PartialPartial } from '@paragon/utilities';

import { LayoutPointPickingTarget } from '../../../store/slices/applicationSlice';
import { LayoutPointPickerField } from '../../interface/subpanels/insertions/truss-envelope/LayoutPointPickerField';

type EditableRectangle2D = Pick<
  Rectangle2D,
  'position' | 'verticalAnchor' | 'horizontalAnchor' | 'width' | 'length' | 'rotationAngle'
>;

type EditedRectangle2D = Partial<
  Pick<Rectangle2D, 'verticalAnchor' | 'horizontalAnchor' | 'width' | 'length' | 'rotationAngle'>
>;

interface Props {
  readonly value: PartialPartial<EditableRectangle2D, 'position'>;
  readonly positionTarget: LayoutPointPickingTarget;

  readonly onChange: (updatedValues: EditedRectangle2D) => void;

  readonly disabled?: boolean;
}

export function LayoutRectangleControls({ value, positionTarget, onChange, disabled }: Props) {
  const createOnChange =
    <TProp extends keyof Rectangle2D, TVal extends Rectangle2D[TProp]>(prop: TProp) =>
    (editedValue: TVal) =>
      void onChange({ ...value, [prop]: editedValue });

  return (
    <>
      <Stack direction="row" spacing={1}>
        <DistanceField
          label="Width"
          value={value.width}
          min={1 / 16}
          onSubmit={createOnChange('width')}
          disabled={disabled}
        />
        <DistanceField
          label="Length"
          value={value.length}
          min={1 / 16}
          onSubmit={createOnChange('length')}
          disabled={disabled}
        />
      </Stack>

      <AngleField
        label="Rotation"
        defaultDisplayFormat="degrees"
        disableSwitchingDisplayFormat
        value={value.rotationAngle}
        onSubmit={createOnChange('rotationAngle')}
        disabled={disabled}
      />

      <LayoutPointPickerField label="Position" target={positionTarget} value={value.position} disabled={disabled} />

      <FormControl disabled={disabled}>
        <FormLabel>Horizontal Anchor</FormLabel>

        <ToggleButtonGroup
          fullWidth
          size="small"
          disabled={disabled}
          exclusive
          value={value.horizontalAnchor}
          onChange={(_, value: 'Left' | 'Center' | 'Right' | null) => {
            if (value != null) createOnChange('horizontalAnchor')(value);
          }}
        >
          <ToggleButton value="Left">Left</ToggleButton>
          <ToggleButton value="Center">Center</ToggleButton>
          <ToggleButton value="Right">Right</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>

      <FormControl disabled={disabled}>
        <FormLabel>Vertical Anchor</FormLabel>

        <ToggleButtonGroup
          fullWidth
          size="small"
          disabled={disabled}
          exclusive
          value={value.verticalAnchor}
          onChange={(_, value: 'Upper' | 'Center' | 'Lower' | null) => {
            if (value != null) createOnChange('verticalAnchor')(value);
          }}
        >
          <ToggleButton value="Upper">Upper</ToggleButton>
          <ToggleButton value="Center">Center</ToggleButton>
          <ToggleButton value="Lower">Lower</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
    </>
  );
}
